<script setup>
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from "vue"
import AiSuggestionDropdown from "./AiSuggestionDropdown.vue"
import Dropzone from "./Dropzone.vue"

const INPUT_TYPE_OPTIONS = [
  { value: "number", label: "Number" },
  { value: "text", label: "Text" },
  { value: "boolean", label: "Yes / No" },
]

const VALIDATION_TYPE_OPTIONS = [
  { value: "required", label: "Required" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
  { value: "regex", label: "Regex" },
  { value: "options", label: "Options" },
  { value: "length_min", label: "Min length" },
  { value: "length_max", label: "Max length" },
]

const props = defineProps({
  // JSON-serialised array of existing steps passed by Rails via data-steps="..."
  steps: { type: String, default: "[]" },
  // e.g. "guide_version[steps_attributes]"
  formPrefix: { type: String, required: true },
  // Rails direct-upload endpoint, e.g. rails_direct_uploads_url
  uploadUrl: { type: String, required: true },
  // JSON-serialised array of pending AI suggestions
  aiSuggestions: { type: String, default: "[]" },
  // Auto-save URL (PATCH guide_version path). If blank, auto-save is disabled.
  saveUrl: { type: String, default: "" },
  savingLabel: { type: String, default: "Saving..." },
  savedLabel: { type: String, default: "Changes saved" },
  saveErrorLabel: { type: String, default: "Unable to save changes" },
  unsavedLabel: { type: String, default: "Unsaved changes" },
  changeSummary: { type: String, default: "" },
  changeSummaryLabel: { type: String, default: "Summary" },
  changeSummaryPlaceholder: { type: String, default: "Summary" },
  changeSummaryFieldName: { type: String, default: "guide_version[change_summary]" },
  stepsHeading: { type: String, default: "Steps" },
})

let newStepCounter = 0
let newInputCounter = 0

function createNewStep() {
  return {
    id: null,
    title: "",
    content: "",
    stepNumber: 0,
    hasImage: false,
    imageUrl: null,
    imageAlt: "Step image",
    imageLightboxGroup: "",
    imageSignedId: null,
    imageRemovalPending: false,
    localImageUrl: null,
    payload: { inputs: [] },
    removed: false,
    removeImage: false,
    key: `new-${++newStepCounter}`,
  }
}

function labelToKey(label) {
  const slug = String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .substring(0, 64)
  return slug || `input_${newInputCounter}`
}

function createNewInput() {
  ++newInputCounter
  return {
    key: "",
    label: "",
    inputType: "number",
    validations: [],
  }
}

function createNewValidation() {
  return {
    validationType: "required",
    value: "",
  }
}

function safeJsonParse(raw) {
  if (typeof raw !== "string") return raw
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

function normalizePayload(rawPayload) {
  const payloadObject = safeJsonParse(rawPayload)
  const payload = payloadObject && typeof payloadObject === "object" ? payloadObject : {}
  const rawInputs = Array.isArray(payload.inputs) ? payload.inputs : []

  const inputs = rawInputs
    .filter((rawInput) => rawInput && typeof rawInput === "object")
    .map((rawInput) => {
      const rawValidations = Array.isArray(rawInput.validations) ? rawInput.validations : []
      return {
        key: String(rawInput.key ?? `input_${++newInputCounter}`).trim(),
        label: String(rawInput.label ?? "").trim(),
        inputType: String(rawInput.input_type ?? "number"),
        validations: rawValidations
          .filter((validation) => validation && typeof validation === "object")
          .map((validation) => ({
            validationType: String(validation.validation_type ?? "required"),
            value: validation.value ?? "",
          })),
      }
    })

  return { inputs }
}

function payloadForSubmit(step) {
  const inputs = step.payload.inputs.map((input) => ({
    key: labelToKey(input.label),
    label: input.label,
    input_type: input.inputType,
    validations: input.validations.map((validation) => ({
      validation_type: validation.validationType,
      value: validation.value,
    })),
  }))

  return JSON.stringify({ inputs })
}

const steps = ref(
  JSON.parse(props.steps).map((s) => ({
    id: s.id ?? null,
    title: s.title ?? "",
    content: s.content ?? "",
    stepNumber: s.stepNumber ?? 0,
    hasImage: s.hasImage ?? false,
    imageUrl: s.imageUrl ?? null,
    imageAlt: s.imageAlt ?? "Step image",
    imageLightboxGroup: s.imageLightboxGroup ?? "",
    imageSignedId: s.imageSignedId ?? null,
    imageRemovalPending: false,
    payload: normalizePayload(s.payload),
    localImageUrl: null,
    removed: false,
    removeImage: false,
    key: s.id ? `existing-${s.id}` : `new-${++newStepCounter}`,
  }))
)

// ── Auto-save ──────────────────────────────────────────────────────────────

const isSaving = ref(false)
const saveErrorMessage = ref("")
const changeSummary = ref(props.changeSummary)
let saveTimer = null
let hasQueuedSave = false

function stepsSnapshot() {
  return JSON.stringify(
    {
      changeSummary: changeSummary.value,
      steps: stepsToRender.value.map((s) => ({
        id: s.id,
        title: s.title,
        content: s.content,
        removed: s.removed,
        removeImage: s.removeImage,
        imageSignedId: s.imageSignedId,
        payload: s.payload,
      })),
    }
  )
}

const lastSaved = ref("")
const hasPendingChanges = computed(() => {
  if (!props.saveUrl) return false
  return stepsSnapshot() !== lastSaved.value
})

function scheduleSave() {
  if (!props.saveUrl) return
  saveErrorMessage.value = ""
  if (saveTimer) window.clearTimeout(saveTimer)
  saveTimer = window.setTimeout(() => {
    saveTimer = null
    saveNow()
  }, 800)
}

function staleStepIdFromPayload(payload) {
  if (payload?.staleStepId) return Number(payload.staleStepId)
  const message = payload?.errors?.join(" ") || ""
  const match = message.match(/ID=(\d+)/)
  return match ? Number(match[1]) : null
}

function reconcileAfterSave(payload) {
  if (!Array.isArray(payload.steps)) return

  const serverByStepNumber = new Map(payload.steps.map((s) => [s.stepNumber, s.id]))
  const serverIds = new Set(payload.steps.map((s) => s.id))

  // Existing removed steps that no longer exist on server are permanently deleted.
  steps.value = steps.value.filter((step) => !(step.removed && step.id && !serverIds.has(step.id)))

  // Assign DB IDs to newly created client-side steps.
  steps.value.forEach((step) => {
    if (!step.id && !step.removed) {
      const displayNum = stepDisplayNumber(step)
      const serverId = serverByStepNumber.get(displayNum)
      if (serverId) {
        step.id = serverId
        step.key = `existing-${serverId}`
      }
    }
  })
}

async function saveNow() {
  if (!props.saveUrl || !hasPendingChanges.value) return
  if (isSaving.value) {
    hasQueuedSave = true
    return
  }

  isSaving.value = true
  saveErrorMessage.value = ""

  const formData = new FormData()
  if (props.changeSummaryFieldName) {
    formData.append(props.changeSummaryFieldName, changeSummary.value)
  }

  stepsToRender.value.forEach((step, renderIndex) => {
    if (step.id) formData.append(fieldName(renderIndex, "id"), String(step.id))
    formData.append(fieldName(renderIndex, "_destroy"), step.removed ? "1" : "0")
    formData.append(fieldName(renderIndex, "step_number"), String(stepDisplayNumber(step)))
    formData.append(fieldName(renderIndex, "title"), step.title)
    formData.append(fieldName(renderIndex, "content"), step.content)
    formData.append(fieldName(renderIndex, "payload"), payloadForSubmit(step))
    formData.append(fieldName(renderIndex, "remove_image"), step.removeImage ? "1" : "0")
    if (step.imageSignedId) {
      formData.append(fieldName(renderIndex, "image"), step.imageSignedId)
    }
  })

  try {
    const response = await fetch(props.saveUrl, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
      },
      credentials: "same-origin",
      body: formData,
    })

    const payload = await response.json()

    if (!response.ok) {
      const staleStepId = staleStepIdFromPayload(payload)
      if (staleStepId) {
        const staleStep = steps.value.find((step) => step.id === staleStepId)
        if (staleStep) {
          if (staleStep.removed) {
            steps.value = steps.value.filter((step) => step !== staleStep)
          } else {
            staleStep.id = null
            staleStep.key = `new-${++newStepCounter}`
          }

          saveErrorMessage.value = ""
          hasQueuedSave = true
          return
        }
      }

      saveErrorMessage.value = payload?.errors?.join(", ") || ""
      return
    }

    reconcileAfterSave(payload)

    lastSaved.value = stepsSnapshot()
  } catch (_) {
    saveErrorMessage.value = ""
  } finally {
    isSaving.value = false
    if (hasQueuedSave) {
      hasQueuedSave = false
      saveNow()
    }
  }
}

// ── AI Suggestions ─────────────────────────────────────────────────────────

const suggestions = ref(JSON.parse(props.aiSuggestions))

function suggestionFor(stepId, field) {
  return suggestions.value.find((s) => s.stepId === stepId && s.field === field) ?? null
}

async function postSuggestionAction(url, suggestion) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content ?? "",
    },
  })
  if (response.ok || response.status === 302 || response.status === 406) {
    suggestions.value = suggestions.value.filter((s) => s.id !== suggestion.id)
    return true
  }
  return false
}

async function applySuggestion(suggestion) {
  const ok = await postSuggestionAction(suggestion.applyUrl, suggestion)
  if (ok) {
    const step = steps.value.find((s) => s.id === suggestion.stepId)
    if (step) {
      if (suggestion.field === "title") step.title = suggestion.suggestedText
      if (suggestion.field === "content") step.content = suggestion.suggestedText
    }
  }
}

async function dismissSuggestion(suggestion) {
  await postSuggestionAction(suggestion.dismissUrl, suggestion)
}

// ── Steps state ─────────────────────────────────────────────────────────────

// Existing steps must always be in the form so Rails can _destroy them.
// New steps that have been removed can be dropped entirely — nothing to persist.
const stepsToRender = computed(() => steps.value.filter((s) => s.id || !s.removed))

const hasAnyActiveStep = computed(() => stepsToRender.value.some((s) => !s.removed))
const activeSteps = computed(() => stepsToRender.value.filter((s) => !s.removed))

// 1-based display number among non-removed steps
function stepDisplayNumber(step) {
  const active = stepsToRender.value.filter((s) => !s.removed)
  return active.indexOf(step) + 1
}

// Rails nested attribute field helpers
function fieldName(renderIndex, field) {
  return `${props.formPrefix}[${renderIndex}][${field}]`
}
function fieldId(renderIndex, field) {
  return `${props.formPrefix}[${renderIndex}][${field}]`.replace(/[[\]]/g, "_")
}

// Step element refs for scroll-into-view
const stepRefMap = new Map()
function setStepRef(step, el) {
  if (el) stepRefMap.set(step, el)
  else stepRefMap.delete(step)
}
function scrollToStep(step) {
  nextTick(() => stepRefMap.get(step)?.scrollIntoView({ behavior: "smooth", block: "center" }))
}

async function scrollToNewStep(step) {
  const stepKey = step.key

  const tryScrollAndFocus = () => {
    const card = document.querySelector(`[data-step-key="${stepKey}"]`)
    if (!card) return false

    card.scrollIntoView({ behavior: "smooth", block: "center" })
    const titleInput = card.querySelector("input[type='text']")
    titleInput?.focus({ preventScroll: true })
    return true
  }

  await nextTick()
  if (tryScrollAndFocus()) return

  requestAnimationFrame(() => {
    if (tryScrollAndFocus()) return
    requestAnimationFrame(() => {
      tryScrollAndFocus()
    })
  })
}

function stepAnchorId(step) {
  return `guide-version-step-${stepDisplayNumber(step)}`
}

function jumpToStep(step) {
  const hash = `#${stepAnchorId(step)}`
  if (window.location.hash !== hash) {
    window.history.replaceState(null, "", hash)
  }
  scrollToStep(step)
}

function handleAiSuggestionsUpdated(event) {
  const updated = event?.detail?.suggestions
  if (!Array.isArray(updated)) return
  suggestions.value = updated
}

function handleAiSuggestionApplied(event) {
  const { stepId, field, suggestedText } = event?.detail || {}
  if (!stepId || !field) return
  const step = steps.value.find((s) => s.id === stepId)
  if (!step) return
  if (field === "title") step.title = suggestedText
  if (field === "content") step.content = suggestedText
}

watch(steps, scheduleSave, { deep: true })
watch(changeSummary, scheduleSave)

onMounted(() => {
  // Snapshot current state so first change is detected correctly
  lastSaved.value = stepsSnapshot()

  window.addEventListener("ai-suggestions-updated", handleAiSuggestionsUpdated)
  window.addEventListener("ai-suggestion-applied", handleAiSuggestionApplied)

  const stepNumberParam = Number.parseInt(new URLSearchParams(window.location.search).get("step_number"), 10)
  if (!Number.isInteger(stepNumberParam) || stepNumberParam <= 0) return

  const targetStep = activeSteps.value.find((step) => stepDisplayNumber(step) === stepNumberParam)
  if (!targetStep) return

  jumpToStep(targetStep)
})

onBeforeUnmount(() => {
  if (saveTimer) window.clearTimeout(saveTimer)
  window.removeEventListener("ai-suggestions-updated", handleAiSuggestionsUpdated)
  window.removeEventListener("ai-suggestion-applied", handleAiSuggestionApplied)
})

// ── Mutations ────────────────────────────────────────────────────────────────

function insertAfter(afterStep) {
  const idx = steps.value.indexOf(afterStep)
  const newStep = createNewStep()
  const arr = [...steps.value]
  arr.splice(idx + 1, 0, newStep)
  steps.value = arr
  scrollToNewStep(newStep)
}

function insertFirst() {
  const newStep = createNewStep()
  steps.value = [newStep, ...steps.value]
  scrollToNewStep(newStep)
}

function findPreviousActiveIndex(step) {
  const idx = steps.value.indexOf(step)
  for (let i = idx - 1; i >= 0; i--) {
    if (!steps.value[i].removed) return i
  }
  return -1
}

function findNextActiveIndex(step) {
  const idx = steps.value.indexOf(step)
  for (let i = idx + 1; i < steps.value.length; i++) {
    if (!steps.value[i].removed) return i
  }
  return -1
}

function moveUp(step) {
  const idx = steps.value.indexOf(step)
  const prevIdx = findPreviousActiveIndex(step)
  if (prevIdx === -1) return
  const arr = [...steps.value]
  ;[arr[idx], arr[prevIdx]] = [arr[prevIdx], arr[idx]]
  steps.value = arr
  scrollToStep(step)
}

function moveDown(step) {
  const idx = steps.value.indexOf(step)
  const nextIdx = findNextActiveIndex(step)
  if (nextIdx === -1) return
  const arr = [...steps.value]
  ;[arr[idx], arr[nextIdx]] = [arr[nextIdx], arr[idx]]
  steps.value = arr
  scrollToStep(step)
}

function confirmAndRemoveStep(step) {
  if (!window.confirm("Delete this step? This action cannot be undone.")) return

  if (step.id) {
    step.removed = true
  } else {
    steps.value = steps.value.filter((s) => s !== step)
  }
}
function markImageForRemoval(step) {
  step.imageRemovalPending = true
}

function cancelImageRemoval(step) {
  step.imageRemovalPending = false
}

function confirmImageRemoval(step) {
  step.imageRemovalPending = false
  step.removeImage = true
  scheduleSave()
  saveNow()
}

function clearImageRemoval(step) {
  step.imageRemovalPending = false
  step.removeImage = false
}

function handleUploadComplete(step, signedId) {
  if (!signedId) return
  step.imageSignedId = signedId
  step.imageRemovalPending = false
  step.removeImage = false
  step.hasImage = true
  scheduleSave()
  saveNow()
}

function addInput(step) {
  step.payload.inputs.push(createNewInput())
}

function removeInput(step, inputIndex) {
  step.payload.inputs.splice(inputIndex, 1)
}

function addValidation(input) {
  input.validations.push(createNewValidation())
}

function removeValidation(input, validationIndex) {
  input.validations.splice(validationIndex, 1)
}

function shouldShowValidationValue(validationType) {
  return validationType !== "required"
}

function validationValuePlaceholder(validationType) {
  if (validationType === "options") return "Comma-separated options"
  if (validationType === "regex") return "Pattern"
  if (validationType === "min" || validationType === "max") return "Numeric value"
  if (validationType === "length_min" || validationType === "length_max") return "Length value"
  return "Value"
}

function normalizeValidationValue(validation) {
  if (validation.validationType === "required") {
    validation.value = ""
    return
  }

  if (validation.validationType === "options") {
    if (Array.isArray(validation.value)) return
    const parts = String(validation.value ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
    validation.value = parts
    return
  }

  if (Array.isArray(validation.value)) {
    validation.value = validation.value.join(",")
  }
}
</script>

<template>
  <div>
  <Teleport v-if="saveUrl" to="#steps-save-status">
    <div class="d-flex align-items-center gap-2">
      <template v-if="isSaving">
        <span class="fa fa-spinner fa-spin text-muted" aria-hidden="true"></span>
        <span class="text-muted">{{ savingLabel }}</span>
      </template>
      <template v-else-if="saveErrorMessage">
        <span class="text-danger">{{ saveErrorLabel }}</span>
      </template>
      <template v-else-if="hasPendingChanges">
        <span class="text-warning-emphasis">{{ unsavedLabel }}</span>
      </template>
      <template v-else>
        <span class="text-success">{{ savedLabel }}</span>
      </template>
    </div>
  </Teleport>
  <div class="offset-md-1 card shadow-sm my-4">
    <div class="card-body">
      <div class="form-floating mb-0">
        <input
          id="guide-version-change-summary"
          v-model="changeSummary"
          type="text"
          class="form-control"
          :name="changeSummaryFieldName"
          :placeholder="changeSummaryPlaceholder"
        >
        <label for="guide-version-change-summary" class="form-label">{{ changeSummaryLabel }}</label>
      </div>
    </div>
  </div>

  <h2 class="h4 mb-3">{{ stepsHeading }}</h2>
  <div class="row g-4 mb-4">
    <aside class="guide-step-index-column col-md-1 d-none d-md-block">
      <nav class="guide-step-index position-sticky" style="top: 1rem;" aria-label="Guide steps">
        <ol class="guide-step-index__list list-unstyled mb-0 d-flex flex-column align-items-center">
          <li v-for="step in activeSteps" :key="`index-${step.key}`">
            <a
              class="guide-step-index__link"
              :href="`#${stepAnchorId(step)}`"
              @click.prevent="jumpToStep(step)"
            >
              {{ stepDisplayNumber(step) }}
            </a>
          </li>
        </ol>
      </nav>
    </aside>

    <div class="col-12 col-md-11 vstack gap-3">
      <template v-for="(step, renderIndex) in stepsToRender" :key="step.key">
        <!--
          Hidden Rails fields are outside v-show so they submit even when the
          card is hidden. For existing+removed, this sends id + _destroy=1 to Rails.
        -->
        <input v-if="step.id" type="hidden" :name="fieldName(renderIndex, 'id')" :value="step.id">
        <input type="hidden" :name="fieldName(renderIndex, '_destroy')" :value="step.removed ? '1' : '0'">

        <!-- Step card -->
        <div
          v-show="!step.removed"
          class="guide-step card shadow-sm"
          :id="stepAnchorId(step)"
          :ref="(el) => setStepRef(step, el)"
          :data-step-id="step.id"
          :data-step-key="step.key"
        >
          <div class="card-body">
            <input
              type="hidden"
              :name="fieldName(renderIndex, 'step_number')"
              :value="stepDisplayNumber(step)"
            >
            <input
              type="hidden"
              :name="fieldName(renderIndex, 'payload')"
              :value="payloadForSubmit(step)"
            >

            <div class="row g-3">
              <!-- Step header: number + reorder buttons + delete -->
              <div class="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-3">
                  <h3 class="fw-semibold text-muted">#{{ stepDisplayNumber(step) }}</h3>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-lg rounded-circle btn-circle d-flex align-items-center justify-content-center"
                    @click.prevent="moveUp(step)"
                    aria-label="Move step up"
                  >
                    <span class="fa fa-arrow-up" aria-hidden="true"></span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-lg rounded-circle btn-circle d-flex align-items-center justify-content-center"
                    @click.prevent="moveDown(step)"
                    aria-label="Move step down"
                  >
                    <span class="fa fa-arrow-down" aria-hidden="true"></span>
                  </button>
                </div>
                <div class="d-flex justify-content-end align-items-center gap-2">
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-lg rounded-circle btn-circle d-flex align-items-center justify-content-center"
                    @click.prevent="confirmAndRemoveStep(step)"
                    aria-label="Delete step"
                  >
                    <span class="fa fa-trash" aria-hidden="true"></span>
                  </button>
                </div>
              </div>

              <!-- Title + content -->
              <div class="col-12 col-md-6 vstack gap-2">
                <div class="position-relative">
                  <input
                    type="text"
                    class="form-control fs-5"
                    :class="{ 'pe-5': step.id && suggestionFor(step.id, 'title') }"
                    placeholder="Step Title"
                    v-model="step.title"
                    :name="fieldName(renderIndex, 'title')"
                    :id="fieldId(renderIndex, 'title')"
                  >
                  <div v-if="step.id && suggestionFor(step.id, 'title')" class="position-absolute top-0 end-0 mt-2 me-2">
                    <AiSuggestionDropdown
                      :suggestion="suggestionFor(step.id, 'title')"
                      :trigger-id="`suggestion-title-${step.id}`"
                      aria-label="View AI suggestion for title"
                      @apply="applySuggestion"
                      @dismiss="dismissSuggestion"
                    />
                  </div>
                </div>
                <div class="position-relative">
                  <textarea
                    class="form-control"
                    rows="4"
                    placeholder="Step Description"
                    v-model="step.content"
                    :name="fieldName(renderIndex, 'content')"
                    :id="fieldId(renderIndex, 'content')"
                  ></textarea>
                  <div v-if="step.id && suggestionFor(step.id, 'content')" class="position-absolute top-0 end-0 mt-2 me-2">
                    <AiSuggestionDropdown
                      :suggestion="suggestionFor(step.id, 'content')"
                      :trigger-id="`suggestion-content-${step.id}`"
                      aria-label="View AI suggestion for content"
                      @apply="applySuggestion"
                      @dismiss="dismissSuggestion"
                    />
                  </div>
                </div>
              </div>

              <!-- Image upload + preview -->
              <div class="col-12 col-md-6">
                <div class="vstack gap-3">
                  <input
                    type="hidden"
                    :name="fieldName(renderIndex, 'remove_image')"
                    :value="step.removeImage ? '1' : '0'"
                  >

                  <Dropzone
                    :input-name="fieldName(renderIndex, 'image')"
                    :input-id="fieldId(renderIndex, 'image')"
                    :default-text="step.hasImage
                      ? 'PNG, JPG, or WEBP. Replace the current image by uploading a new one.'
                      : 'PNG, JPG, or WEBP.'"
                    capture="environment"
                    :direct-upload-url="uploadUrl"
                    @file-change="clearImageRemoval(step)"
                    @preview-ready="(url) => (step.localImageUrl = url)"
                    @upload-complete="(signedId) => handleUploadComplete(step, signedId)"
                  />

                  <!-- Newly selected image preview (local blob URL, shown immediately) -->
                  <div v-if="step.localImageUrl">
                    <img
                      :src="step.localImageUrl"
                      class="img-fluid rounded border"
                      :alt="step.imageAlt"
                    >
                  </div>

                  <!-- Existing server image preview (with fslightbox) -->
                  <div v-else-if="step.hasImage && step.imageUrl && !step.removeImage">
                    <div class="step-image-preview d-inline-block position-relative">
                      <a :href="step.imageUrl" :data-fslightbox="step.imageLightboxGroup">
                        <img
                          :src="step.imageUrl"
                          class="img-fluid rounded border"
                          :alt="step.imageAlt"
                        >
                      </a>
                      <button
                        type="button"
                        class="step-image-preview__remove d-flex align-items-center justify-content-center text-decoration-none text-white"
                        @click.prevent="markImageForRemoval(step)"
                        aria-label="Remove image"
                      >
                        <span aria-hidden="true" class="fa fa-times"></span>
                        <span class="visually-hidden">Remove image</span>
                      </button>
                    </div>
                  </div>

                  <!-- Image removal notice -->
                  <div v-if="step.hasImage && step.imageRemovalPending && !step.localImageUrl" class="d-flex align-items-center gap-2 text-muted small mt-2">
                    <span>Confirm removing this image?</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      @click.prevent="confirmImageRemoval(step)"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click.prevent="cancelImageRemoval(step)"
                    >
                      Cancel
                    </button>
                  </div>

                  <p v-else-if="step.hasImage && step.removeImage && !step.localImageUrl" class="text-muted small mt-2 mb-0">
                    Image removal confirmed.
                  </p>
                </div>
              </div>

              <hr>

              <div class="col-12 mt-1">

                <h4 v-if="step.payload.inputs.length > 0" class="small fw-semibold mb-3">Inputs</h4>

                <div v-if="step.payload.inputs.length > 0" class="table-responsive">
                  <table class="table table-sm table-bordered align-middle mb-0">
                    <thead class="table-light">
                      <tr>
                        <th class="small fw-semibold">Label</th>
                        <th class="small fw-semibold" style="width:8rem;">Type</th>
                        <th class="small fw-semibold">Validation rules</th>
                        <th style="width:2.5rem;"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(input, inputIndex) in step.payload.inputs"
                        :key="`${step.key}-input-${inputIndex}`"
                      >
                        <td>
                          <input
                            type="text"
                            style="min-width:10rem;"
                            class="form-control form-control-sm"
                            v-model="input.label"
                            placeholder="e.g. Sensor reading"
                          >
                        </td>

                        <td>
                          <select class="form-select form-select-sm" style="min-width:max-content;" v-model="input.inputType">
                            <option
                              v-for="option in INPUT_TYPE_OPTIONS"
                              :key="option.value"
                              :value="option.value"
                            >{{ option.label }}</option>
                          </select>
                        </td>

                        <td>
                          <div class="vstack gap-1">
                            <div
                              v-for="(validation, validationIndex) in input.validations"
                              :key="`${step.key}-input-${inputIndex}-val-${validationIndex}`"
                              class="d-flex align-items-center gap-1 flex-nowrap"
                            >
                              <select
                                class="form-select form-select-sm flex-shrink-0"
                                style="width:auto;"
                                v-model="validation.validationType"
                                @change="normalizeValidationValue(validation)"
                              >
                                <option
                                  v-for="option in VALIDATION_TYPE_OPTIONS"
                                  :key="option.value"
                                  :value="option.value"
                                >{{ option.label }}</option>
                              </select>
                              <input
                                v-if="shouldShowValidationValue(validation.validationType)"
                                type="text"
                                class="form-control form-control-sm flex-shrink-0 flex-grow-1"
                                style="width:7rem;"
                                :placeholder="validationValuePlaceholder(validation.validationType)"
                                :value="Array.isArray(validation.value) ? validation.value.join(', ') : validation.value"
                                @input="validation.value = $event.target.value"
                                @blur="normalizeValidationValue(validation)"
                              >
                              <button
                                type="button"
                                class="btn btn-sm btn-link text-danger p-0 flex-grow-0"
                                @click.prevent="removeValidation(input, validationIndex)"
                                aria-label="Remove rule"
                              >
                                <span class="fa fa-times" aria-hidden="true"></span>
                              </button>
                            </div>
                            <button
                              type="button"
                              class="btn btn-link btn-sm p-0 text-start small"
                              @click.prevent="addValidation(input)"
                            >
                              + Add rule
                            </button>
                          </div>
                        </td>

                        <td class="text-center">
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            @click.prevent="removeInput(step, inputIndex)"
                            aria-label="Remove input"
                          >
                            <span class="fa fa-trash" aria-hidden="true"></span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="mt-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click.prevent="addInput(step)"
                  >
                    + Add input
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Insert after button (only for visible steps) -->
        <div v-if="!step.removed" class="d-flex justify-content-center">
          <button
            type="button"
            class="btn btn-outline-primary btn-lg px-5"
            @click.prevent="insertAfter(step)"
            :aria-label="`Insert a step after step ${stepDisplayNumber(step)}`"
          >
            + Add step
          </button>
        </div>
      </template>

      <!-- Empty-state add button (no visible steps) -->
      <div v-if="!hasAnyActiveStep" class="d-flex justify-content-center">
        <button
          type="button"
          class="btn btn-outline-primary btn-lg px-5"
          @click.prevent="insertFirst"
        >
          + Add step
        </button>
      </div>
    </div>
  </div>
  </div>
</template>
